<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.trainings.editTitle') }}</h2>
        <button type="button" class="btn-close" @click="$emit('close')">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-banner">{{ error }}</div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelTitle') }} <span class="required">*</span></label>
          <input v-model="form.title" type="text" class="input" maxlength="255" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.common.description') }} <span class="required">*</span></label>
          <textarea v-model="form.description" class="textarea" rows="3" maxlength="2000" />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelCredits') }} <span class="required">*</span></label>
            <input v-model.number="form.credit_cost" type="number" class="input" min="0" step="0.01" />
          </div>
          <div class="form-group">
            <label>{{ $t('bo.trainings.colLevel') }}</label>
            <select v-model="form.level" class="input">
              <option value="">{{ $t('bo.trainings.chooseLevel') }}</option>
              <option value="beginner">{{ $t('bo.trainings.levelBeginner') }}</option>
              <option value="intermediate">{{ $t('bo.trainings.levelIntermediate') }}</option>
              <option value="advanced">{{ $t('bo.trainings.levelAdvanced') }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelCategory') }}</label>
            <input v-model="form.category" type="text" class="input" maxlength="100" />
          </div>
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelProvider') }}</label>
            <input v-model="form.provider" type="text" class="input" maxlength="255" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelDuration') }}</label>
            <input v-model.number="form.duration_minutes" type="number" class="input" min="0" />
          </div>
          <div class="form-group">
            <label>{{ $t('bo.trainings.labelType') }}</label>
            <select v-model="form.training_type" class="input">
              <option value="">{{ $t('bo.trainings.chooseType') }}</option>
              <option value="online">{{ $t('bo.trainings.typeOnline') }}</option>
              <option value="in_person">{{ $t('bo.trainings.typeInPerson') }}</option>
              <option value="hybrid">{{ $t('bo.trainings.typeHybrid') }}</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelUrl') }}</label>
          <input v-model="form.url" type="url" class="input" maxlength="500" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelThumbnail') }}</label>
          <CoverImageInput v-model="form.thumbnail_url" v-model:media-id="form.thumbnail_id" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.trainings.labelLanguages') }}</label>
          <input v-model="form.languagesText" type="text" class="input" :placeholder="$t('bo.trainings.languagesPlaceholder')" />
        </div>

        <!-- Localisation : générer les autres langues via le moteur, puis éditer -->
        <div v-if="otherLocales.length" class="localization">
          <div class="loc-head">
            <h3 class="loc-title">{{ $t('bo.trainings.localizationTitle') }}</h3>
            <button type="button" class="btn-generate" :disabled="localizing" @click="generate">
              {{ localizing ? $t('bo.trainings.generating') : $t('bo.trainings.generateTranslations') }}
            </button>
          </div>
          <p class="loc-hint">{{ $t('bo.trainings.localizationHint') }}</p>

          <div class="loc-tabs">
            <button
              v-for="l in otherLocales"
              :key="l.code"
              type="button"
              class="loc-tab"
              :class="{ active: activeLocale === l.code }"
              @click="activeLocale = l.code"
            >
              {{ l.label || l.code }}
              <span v-if="isAuto(l.code)" class="auto-badge">{{ $t('bo.trainings.autoBadge') }}</span>
            </button>
          </div>

          <template v-if="activeLocale && translations[activeLocale]">
            <div class="form-group">
              <label>{{ $t('bo.trainings.labelTitle') }}</label>
              <input v-model="translations[activeLocale].title" type="text" class="input" maxlength="255" />
            </div>
            <div class="form-group">
              <label>{{ $t('bo.common.description') }}</label>
              <textarea v-model="translations[activeLocale].description" class="textarea" rows="3" maxlength="2000" />
            </div>
            <div class="form-group">
              <label>{{ $t('bo.trainings.labelUrl') }}</label>
              <input v-model="translations[activeLocale].url" type="url" class="input" maxlength="500" />
              <p class="field-hint">{{ $t('bo.trainings.localizationUrlHint') }}</p>
            </div>
          </template>
        </div>

        <label class="checkbox-row">
          <input v-model="form.is_active" type="checkbox" />
          {{ $t('bo.trainings.labelActive') }}
        </label>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn-cancel" @click="$emit('close')">{{ $t('bo.common.cancel') }}</button>
        <button type="button" class="btn-submit" :disabled="loading || !canSubmit" @click="submit">
          {{ loading ? $t('bo.common.saving') : $t('bo.common.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import trainingAdminService from '@/services/trainingAdminService'
import translationService from '@/services/translationService'
import CoverImageInput from '@/components/ui/CoverImageInput.vue'

const { t } = useI18n()

const props = defineProps({
  training: { type: Object, required: true },
})
const emit = defineEmits(['close', 'updated'])

const loading = ref(false)
const error = ref('')

// --- Localisation (langues autres que la langue source) ---
const locales = ref([])
const defaultLocale = ref('en')
const translations = ref({}) // { [code]: { title, description, url, is_auto_generated } }
const original = ref({}) // snapshot pour détecter les éditions
const activeLocale = ref('')
const localizing = ref(false)

const otherLocales = computed(() => locales.value.filter((l) => l.code !== defaultLocale.value))

const isAuto = (code) => !!translations.value[code]?.is_auto_generated

const loadLocales = async () => {
  try {
    const data = await translationService.getLocales()
    locales.value = Array.isArray(data) ? data : []
    defaultLocale.value = locales.value.find((l) => l.is_default)?.code || 'en'
  } catch {
    locales.value = []
  }
}

const loadTranslations = async () => {
  try {
    const res = await trainingAdminService.getTranslations(props.training.id)
    const rows = res.data?.data || []
    const map = {}
    rows.forEach((r) => {
      map[r.locale] = {
        title: r.title || '',
        description: r.description || '',
        url: r.url || '',
        is_auto_generated: !!r.is_auto_generated,
      }
    })
    translations.value = map
    original.value = JSON.parse(JSON.stringify(map))
  } catch {
    // Section localisation non bloquante : reste vide en cas d'échec.
  }
}

const generate = async () => {
  localizing.value = true
  error.value = ''
  try {
    await trainingAdminService.localize(props.training.id)
    await loadTranslations()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.trainings.failedLocalize')
  } finally {
    localizing.value = false
  }
}

onMounted(async () => {
  await loadLocales()
  await loadTranslations()
  if (!activeLocale.value) activeLocale.value = otherLocales.value[0]?.code || ''
})

const languagesFrom = (value) => {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) return parsed.join(', ')
    } catch {
      return value
    }
  }
  return ''
}

const form = ref({
  title: props.training.title || '',
  description: props.training.description || '',
  credit_cost: props.training.credit_cost ?? 0,
  level: props.training.level || '',
  category: props.training.category || '',
  provider: props.training.provider || '',
  duration_minutes: props.training.duration_minutes ?? null,
  training_type: ['online', 'in_person', 'hybrid'].includes(props.training.training_type)
    ? props.training.training_type
    : '',
  url: props.training.url || '',
  // Ignore bare bucket roots already saved (e.g. .../minio-s3/) so Save can clear them.
  thumbnail_url: (() => {
    const url = props.training.thumbnail_url || ''
    try {
      const segments = new URL(url).pathname.split('/').filter(Boolean)
      return segments.length >= 2 ? url : ''
    } catch {
      return ''
    }
  })(),
  thumbnail_id: (() => {
    const url = props.training.thumbnail_url || ''
    try {
      const segments = new URL(url).pathname.split('/').filter(Boolean)
      return segments.length >= 2 ? (props.training.thumbnail_id ?? null) : null
    } catch {
      return null
    }
  })(),
  languagesText: languagesFrom(props.training.languages) || 'en',
  is_active: props.training.is_active !== false,
})

const canSubmit = computed(
  () =>
    form.value.title.trim() &&
    form.value.description.trim() &&
    form.value.credit_cost !== null &&
    form.value.credit_cost !== '' &&
    Number(form.value.credit_cost) >= 0
)

const buildPayload = () => {
  const languages = form.value.languagesText
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean)

  const payload = {
    title: form.value.title.trim(),
    description: form.value.description.trim(),
    credit_cost: Number(form.value.credit_cost),
    is_active: !!form.value.is_active,
  }

  if (form.value.level) payload.level = form.value.level
  if (form.value.category.trim()) payload.category = form.value.category.trim()
  if (form.value.provider.trim()) payload.provider = form.value.provider.trim()
  if (form.value.training_type.trim()) payload.training_type = form.value.training_type.trim()
  if (form.value.url.trim()) payload.url = form.value.url.trim()
  // Toujours envoyer thumbnail_url (URL ou null) pour refleter le champ : ne plus
  // laisser trainer l'image par defaut, et permettre de retirer une image.
  // thumbnail_id suit l'upload Media ; sans id on laisse le backend clear le Media stale.
  const thumbnailUrl = form.value.thumbnail_url.trim()
  payload.thumbnail_url = thumbnailUrl || null
  payload.thumbnail_id = thumbnailUrl && form.value.thumbnail_id ? Number(form.value.thumbnail_id) : null
  if (form.value.duration_minutes !== null && form.value.duration_minutes !== '') {
    payload.duration_minutes = Number(form.value.duration_minutes)
  }
  if (languages.length) payload.languages = languages

  return payload
}

const submit = async () => {
  if (!canSubmit.value) return
  loading.value = true
  error.value = ''
  try {
    await trainingAdminService.update(props.training.id, buildPayload())

    // Persiste les traductions éditées (langues autres que la source).
    for (const l of otherLocales.value) {
      const cur = translations.value[l.code]
      const ori = original.value[l.code]
      const changed =
        cur &&
        (!ori ||
          cur.title !== ori.title ||
          cur.description !== ori.description ||
          cur.url !== ori.url)
      if (changed && (cur.title?.trim() || cur.description?.trim() || cur.url?.trim())) {
        await trainingAdminService.saveTranslation(props.training.id, l.code, {
          title: cur.title?.trim() || null,
          description: cur.description?.trim() || null,
          url: cur.url?.trim() || null,
        })
      }
    }

    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.trainings.failedUpdate')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.required {
  color: #dc2626;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.input,
.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
  background: white;
  font-family: inherit;
}

.textarea {
  resize: vertical;
}

.input:focus,
.textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.localization {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: #f9fafb;
}

.loc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.loc-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.loc-hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
}

.field-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0;
}

.btn-generate {
  padding: 0.4rem 0.9rem;
  border: 1px solid #3b82f6;
  background: white;
  color: #2563eb;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.btn-generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loc-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.loc-tab {
  padding: 0.3rem 0.7rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.loc-tab.active {
  border-color: #3b82f6;
  color: #2563eb;
  background: #eff6ff;
}

.auto-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #92400e;
  background: #fef3c7;
  padding: 0.05rem 0.35rem;
  border-radius: 9999px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel,
.btn-submit {
  padding: 0.5rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.btn-submit {
  background: #3b82f6;
  border: none;
  color: white;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
